import React, { useCallback, useEffect, useState } from 'react';
import SubmissionTable from './SubmissionTable';
import Pagination from 'reusable/pagination';
import Toolbar from './Toolbar';
import { ACCESS_TOKEN } from 'config/keywords';
import axiosInstance from 'config/axiosInstance';
import { useTranslation } from 'react-i18next';

interface Record {
  id: number;
  name: string;
  phone: string;
  email: string;
  alcoholtests: {
    id: number;
    data: string;
  }[];
  profile_picture?: string;
}

const SubmissionRecent: React.FC = () => {
  const { t } = useTranslation('dashboard');
  const rowsPerPage = 10;
  const [count, setCount] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [sortValue, setSortValue] = useState('');
  const [dateValue, setDateValue] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [records, setRecords] = useState<Record[]>([]);

  const fetchProfilePicture = async (id: number) => {
    const accessToken = localStorage.getItem(ACCESS_TOKEN);
    if (!accessToken) {
      console.error('No access Token');
      return null;
    }

    try {
      const response = await axiosInstance.get(`/v1/drivers/${id}/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      return response.data.profile_picture;
    } catch (error) {
      console.error(`Failed to fetch profile picture for driver ${id}`, error);
      return null;
    }
  };

  const fetchData = useCallback(async () => {
    const accessToken = localStorage.getItem(ACCESS_TOKEN);
    if (!accessToken) {
      console.error('No access Token');
      return;
    }

    const apiUrl = `/v1/drivers/recent_submission/?alcoholtest__testing_time__gte=${dateValue}T00%3A00%3A00.000000Z&alcoholtest__testing_time__lte=${dateValue}T23%3A59%3A59.999999Z&order_by=${sortValue}&search=${searchValue}&page=${currentPage}`;

    try {
      const response = await axiosInstance.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      const fetchedRecords = response.data.results;

      const updatedRecords = await Promise.all(
        fetchedRecords.map(async (record: Record) => {
          const profilePicture = await fetchProfilePicture(record.id);
          return { ...record, profile_picture: profilePicture };
        })
      );

      setRecords(updatedRecords);
      setCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch data', error);
    }
  }, [searchValue, sortValue, dateValue, currentPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const handleSortChange = (value: string) => {
    setSortValue(value);
    setCurrentPage(1);
  };

  const handleDateChange = (value: string) => {
    setDateValue(value);
    setCurrentPage(1);
  };

  const handlePageChange = (value: number) => {
    setCurrentPage(value);
  };

  return (
    <div className="flex flex-col bg-white rounded-md p-4">
      {/* Toolbar */}
      <div className="flex flex-row justify-between items-center">
        <h4 className="text-subtitle-1 font-medium">{t('table.title')}</h4>
        <Toolbar
          searchValue={searchValue}
          sortValue={sortValue}
          dateValue={dateValue}
          currentPage={currentPage}
          onSearchChange={handleSearchChange}
          onSortChange={handleSortChange}
          onDateChange={handleDateChange}
          onPageChange={handlePageChange}
        />
      </div>
      {/* Table */}
      <SubmissionTable records={records} />
      {/* Pagination Section */}
      <Pagination
        allRows={count}
        rowsPerPage={rowsPerPage}
        currentPage={currentPage}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default SubmissionRecent;